import {
  FlexBoxImageScaleView,
  FlexBoxView,
  getFlexNode,
} from '@play-co/timestep-core/flexbox';
import uiConfig from 'src/lib/ui/config';
import i18n from 'src/lib/i18n/i18n';
import animate from '@play-co/timestep-core/lib/animate';
import { animDuration, isPlatformGuest } from 'src/lib/utils';
import { ImageView, View } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import FriendsActionButton from './FriendsActionButton';
import sounds from 'src/lib/sounds';
import StateObserver from 'src/StateObserver';
import {
  hasMetGiftsUnlockCriteria,
  isGiftsFeatureUnlocked,
} from '../gifts/helpers';
import { VisitRecord } from '../../replicant/visit/record';
import { Pet } from 'src/replicant/buruburu/pet';
import { Social } from 'src/replicant/social';
import features from 'src/replicant/ruleset/features';
import { Duo } from 'src/replicant/duo';
import { createEmitter } from 'src/lib/Emitter';
import platform from '@play-co/gcinstant';

const ui = uiConfig.buruburu.menu;

export default class FriendsMenu extends FlexBoxImageScaleView {
  constructor() {
    super(ui.menu);

    this.image.onInputStart = () => this.emit('Close');
    const onSelect = this.onSelect.bind(this);
    const closeMenu = this.closeMenu.bind(this);

    const { user } = StateObserver.getState();
    const isDuoEnabled = isPlatformGuest(platform) || features(user).duo;

    const firstRow = this.addSubview(
      new FlexBoxView(
        isDuoEnabled
          ? ui.menuRow
          : {
              ...ui.menuRow,
              flex: {
                ...ui.menuRow.flex,
                justifyContent: 'flex-end',
                paddingRight: (ui.menuRow.width - 3 * ui.menuButton.width) / 4,
              },
            },
      ),
    );

    const isDuoLocked = isPlatformGuest(platform)
      ? !Duo.api.hasMetUnlockCriteria(user)
      : !Duo.api.isUnlocked(user);

    if (isDuoEnabled) {
      firstRow.addSubview(
        new FriendsActionButton({
          type: 'duoReceivedList',
          onSelect,
          closeMenu,
          isLocked: isDuoLocked,
        }),
      );
    }

    const visitorList = firstRow.addSubview(
      new FriendsActionButton({
        type: 'visitorList',
        onSelect,
        closeMenu,
      }),
    );

    const secondRow = this.addSubview(new FlexBoxView(ui.menuRow));

    const isGiftLocked = isPlatformGuest(platform)
      ? !hasMetGiftsUnlockCriteria(user)
      : !isGiftsFeatureUnlocked(user);
    const giftIcon = new FriendsActionButton({
      type: 'gift',
      onSelect,
      closeMenu,
      isLocked: isGiftLocked,
    });

    if (isDuoEnabled) {
      firstRow.addSubview(giftIcon);
      secondRow.addSubview(
        new FriendsActionButton({
          type: 'duoSentList',
          onSelect,
          closeMenu,
          isLocked: isDuoLocked,
        }),
      );
    } else {
      secondRow.addSubview(giftIcon);
    }

    const isInviteLocked = isPlatformGuest(platform)
      ? !Social.getters(user).hasMetUnlockCriteria()
      : !Social.getters(user).isFriendInviteAvailable();
    const invite = new FriendsActionButton({
      type: 'invite',
      onSelect,
      closeMenu,
      isLocked: isInviteLocked,
    });

    const visitFriend = new FriendsActionButton({
      type: 'visitFriend',
      onSelect,
      closeMenu,
    });

    if (isDuoEnabled) {
      secondRow.addSubview(visitFriend);
      secondRow.addSubview(invite);
    } else {
      secondRow.addSubview(invite);
      secondRow.addSubview(visitFriend);
    }

    createEmitter(this, ({ user }) => ({
      isFriendVisitDisabled: isPlatformGuest(platform)
        ? !VisitRecord.getters.hasMetUnlockCriteria(user)
        : !VisitRecord.getters.isUnlocked(user),
      isDead:
        Pet.getters(user).getPetState(StateObserver.now(), false) === 'dead',
      isSleeping: Pet.getters(user).shouldBeAsleep(StateObserver.now()),
    })).addListener(({ isDead, isFriendVisitDisabled, isSleeping }) => {
      if (isDead) {
        visitorList.setLocked(true, 'dead');
        visitFriend.setLocked(true, 'dead');
      } else if (isSleeping) {
        visitorList.setLocked(true, 'sleeping');
        visitFriend.setLocked(true, 'sleeping');
      } else {
        visitorList.setLocked(isFriendVisitDisabled, 'visitFriend');
        visitFriend.setLocked(isFriendVisitDisabled, 'visitFriend');
      }
    });

    const closeHint = this.addSubview(new ImageView(ui.closeHintBg));
    closeHint.canHandleEvents(false, true);

    getFlexNode(closeHint).updateStyle({ marginTop: 23 });
    closeHint.addSubview(
      new LangBitmapFontTextView({
        ...ui.closeHintText,
        localeText: () => i18n('footer.tapToClose'),
        width: closeHint.style.width,
        height: closeHint.style.height,
      }),
    );
  }

  public fadeIn(superview: View) {
    animate(this)
      .clear()
      .now(() =>
        this.updateOpts({
          superview,
          visible: true,
          opacity: 0,
          offsetY: 100,
        }),
      )
      .then({ opacity: 1, offsetY: 0 }, animDuration, animate.easeOut);
  }

  public fadeOut() {
    animate(this)
      .clear()
      .then({ opacity: 0, offsetY: 100 }, animDuration, animate.easeOut)
      .then(() => {
        this.removeFromSuperview();
      });
  }

  private onSelect() {
    sounds.sfx.play('ok');
  }

  private closeMenu() {
    this.emit('Close');
  }
}
